"use client"

import type { ComponentProps, ElementType, ReactNode } from "react"
import { useRef } from "react"
import { useGSAP } from "@gsap/react"
import cn from "classnames"
import { gsap } from "gsap"

import { usePresence } from "~/components/foundations/AnimatePresence/usePresence"
import { useScroll } from "~/components/foundations/ScrollProvider"

import s from "./TransitionContainer.module.scss"

// Flipped by the first container to mount on the client. The initial route is
// already in the server-rendered HTML at full opacity, so fading it in would
// flash; every route mounted after hydration is new content and does animate.
let hasMountedOnce = false

type Props = {
  className?: string
  children: ReactNode
} & ComponentProps<ElementType>

/**
 * One route's pane inside the persistent SmoothScrollWrapper. Panes share a
 * grid cell, so during a crossfade the exiting and entering routes overlap in
 * one shared scroll container instead of each owning a fixed viewport div.
 *
 * Scroll choreography relies on effect order: AnimatePresence renders the
 * exiting pane before the entering one, and useGSAP runs layout effects in
 * tree order, so the exiting pane reads the scroll position and freezes
 * itself (translateY) before the entering pane resets scroll to the top —
 * all before paint.
 */
export const TransitionContainer = ({ className, children }: Props) => {
  const { isPresent, safeToRemove } = usePresence()
  const { lenis } = useScroll()

  const wrapperRef = useRef<HTMLDivElement>(null)

  // Read via ref inside the effect so a late `setLenis` doesn't re-trigger
  // the enter/exit animation.
  const lenisRef = useRef(lenis)
  lenisRef.current = lenis

  useGSAP(
    () => {
      if (isPresent) {
        if (!hasMountedOnce) {
          hasMountedOnce = true
          return
        }

        lenisRef.current?.scrollTo(0, { immediate: true, force: true })

        gsap.fromTo(
          wrapperRef.current,
          { opacity: 0 },
          {
            opacity: 1,
            duration: 0.5,
          },
        )
      } else {
        // Compensate for the entering pane's scroll reset: shift this pane up
        // by the current scroll offset so it keeps showing the same content
        // while it fades out.
        gsap.set(wrapperRef.current, { y: -(lenisRef.current?.scroll ?? 0) })
        gsap.to(wrapperRef.current, {
          opacity: 0,
          duration: 0.5,
          onComplete: safeToRemove,
        })
      }
    },
    {
      dependencies: [isPresent, safeToRemove],
      scope: wrapperRef,
    },
  )

  return (
    <div ref={wrapperRef} className={cn(s.contentWrapper, className)}>
      {children}
    </div>
  )
}
